/**
 * UploadPanel · Hidden file-input plus visible batch and single-file upload UI.
 *
 * Byte-verbatim extraction of the upload surface that used to live at:
 *   - lines 2895-2919 of KnowledgeGraphPage.jsx (hidden file-input node)
 *   - lines 2941-3064 of KnowledgeGraphPage.jsx (batch panel plus single-file panel)
 *
 * Zero visual change · every data-testid preserved · every className and
 * inline style preserved.
 *
 * Extracted 2026-08-01 · IJ-244 · Phase 5C of the KnowledgeGraphPage.jsx split
 * (see /app/memory/design_drafts/kg_page_split_map.md Section Three Boundary Two).
 * Umbrella: IJ-076 Predictive Text Rebuild continuation · Refactor Programme 2026.
 * Owner: E1.
 *
 * The hidden file-input keeps the same DOM id 'file-input' so the KgHeader
 * upload button (Phase 5B) continues to trigger it via
 * document.getElementById('file-input').click(). The isAdmin gating on the
 * input (SPD v5.76 · 2026-07-12) is preserved verbatim.
 *
 * Props:
 *   - isAdmin · boolean · gates the hidden file-input render.
 *   - selectedFile · file-or-null · from useUploadFlow.
 *   - selectedFiles · array · from useUploadFlow.
 *   - uploadPartition · string · from useUploadFlow.
 *   - batchProgress · object-or-null · from useUploadFlow.
 *   - isUploading · boolean · from useUploadFlow.
 *   - awaitingForecastFile · boolean · parent-owned · suppresses the panels
 *       when the Excel forecast flow is asking the user to pick a file.
 *   - isForecastLoading · boolean · parent-owned · disables the upload
 *       buttons while a forecast is running.
 *   - setSelectedFile · setter · from useUploadFlow · file-input onChange.
 *   - setSelectedFiles · setter · from useUploadFlow · file-input onChange.
 *   - setUploadPartition · setter · from useUploadFlow · partition inputs.
 *   - setBatchProgress · setter · from useUploadFlow · Clear button reset.
 *   - onFileUpload · function · from useUploadFlow · single-file upload.
 *   - onBatchUpload · function · from useUploadFlow · batch upload.
 */
import React from 'react';
import { Button } from '../ui/button';

export const UploadPanel = ({
  isAdmin,
  selectedFile,
  selectedFiles,
  uploadPartition,
  batchProgress,
  isUploading,
  awaitingForecastFile,
  isForecastLoading,
  setSelectedFile,
  setSelectedFiles,
  setUploadPartition,
  setBatchProgress,
  onFileUpload,
  onBatchUpload,
}) => {
  return (
    <>
      {/* Hidden elements.
          D2 · SPD v5.76 (2026-07-12) — wrapped in `isAdmin` so the
          hidden file-input can't be triggered programmatically by
          non-admin users (e.g. via `document.getElementById(...).click()`
          from the browser console). The visible upload button already
          gates on isAdmin at click time; this closes the belt-and-braces
          door too. */}
      {isAdmin && (
        <input
          id="file-input"
          type="file"
          multiple
          accept=".pdf,.docx,.xlsx,.pptx,.txt,.jpg,.jpeg,.png"
          onChange={(e) => {
            const arr = Array.from(e.target.files || []);
            setSelectedFiles(arr);
            // Preserve single-file forecast flow: first file also lives
            // in selectedFile so the Excel forecast codepath continues
            // to work without change.
            setSelectedFile(arr[0] || null);
          }}
          className="hidden"
          data-testid="kg-file-input"
        />
      )}

      {/* Selected file info — batch mode when >1 file selected,
          single-file mode otherwise. Excel forecast flow is
          driven separately from `selectedFile`. */}
      {selectedFiles.length > 1 && !awaitingForecastFile && (
        <div
          data-testid="kg-batch-upload-panel"
          className="max-w-4xl mx-auto mb-4 p-4 bg-gray-800/80 rounded-lg backdrop-blur-sm mx-6"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-white font-medium">
              <span data-testid="kg-batch-file-count">{selectedFiles.length}</span> files
              selected for batch upload to Knowledge Graph
            </p>
            <button
              type="button"
              onClick={() => {
                setSelectedFiles([]);
                setSelectedFile(null);
                document.getElementById('file-input').value = '';
                setBatchProgress(null);
              }}
              data-testid="kg-batch-clear-btn"
              className="text-xs text-gray-400 underline hover:text-gray-200"
            >
              Clear
            </button>
          </div>
          <ul className="mt-2 max-h-24 overflow-auto text-xs text-gray-300 space-y-[1px]">
            {selectedFiles.slice(0, 12).map((f, i) => (
              <li key={i} data-testid="kg-batch-file-item">
                · {f.name}{' '}
                <span className="text-gray-500">
                  ({(f.size / 1024).toFixed(1)} KB)
                </span>
              </li>
            ))}
            {selectedFiles.length > 12 && (
              <li className="text-gray-500 italic">
                …and {selectedFiles.length - 12} more
              </li>
            )}
          </ul>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label
              htmlFor="kg-batch-partition-input"
              className="text-[11px] uppercase tracking-widest text-gray-400"
            >
              Partition
            </label>
            <input
              id="kg-batch-partition-input"
              data-testid="kg-batch-partition-input"
              type="text"
              value={uploadPartition}
              onChange={(e) => setUploadPartition(e.target.value)}
              disabled={isUploading}
              placeholder="searchinc_thesis_2026"
              className="rounded border border-gray-600 bg-gray-900 px-2 py-1 text-sm text-white focus:border-cyan-400 focus:outline-none"
            />
            <span className="text-[10px] italic text-gray-500">
              a-z 0-9 _ · max 64 chars · lands the batch under one tenant
            </span>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <Button
              onClick={onBatchUpload}
              disabled={isUploading || isForecastLoading}
              data-testid="kg-batch-upload-btn"
              className="bg-cyan-600 hover:bg-cyan-500"
            >
              {isUploading
                ? `Uploading ${(batchProgress?.done ?? 0)} / ${selectedFiles.length}…`
                : `Upload ${selectedFiles.length} files to Knowledge Graph`}
            </Button>
            {batchProgress && (
              <div
                data-testid="kg-batch-progress"
                className="text-xs text-gray-300"
              >
                <div>
                  {batchProgress.done} / {batchProgress.total}
                  {' '}· ok {batchProgress.ok}
                  {' '}· failed {batchProgress.failed}
                </div>
                {batchProgress.currentName && (
                  <div className="text-cyan-300/80 italic">
                    Now: {batchProgress.currentName}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Single-file banner — unchanged from the existing flow;
          only rendered when exactly one file is selected. */}
      {selectedFile && selectedFiles.length <= 1 && !awaitingForecastFile && (
        <div className="max-w-4xl mx-auto mb-4 p-4 bg-gray-800/80 rounded-lg backdrop-blur-sm mx-6">
          <p className="text-sm text-white font-medium">Selected: {selectedFile.name}</p>
          <p className="text-xs text-gray-400">Size: {(selectedFile.size / 1024).toFixed(1)} KB</p>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <label
              htmlFor="kg-single-partition-input"
              className="text-[11px] uppercase tracking-widest text-gray-400"
            >
              Partition
            </label>
            <input
              id="kg-single-partition-input"
              data-testid="kg-single-partition-input"
              type="text"
              value={uploadPartition}
              onChange={(e) => setUploadPartition(e.target.value)}
              disabled={isUploading}
              placeholder="searchinc_thesis_2026"
              className="rounded border border-gray-600 bg-gray-900 px-2 py-1 text-sm text-white focus:border-cyan-400 focus:outline-none"
            />
            <Button onClick={onFileUpload} disabled={isUploading || isForecastLoading} className="bg-cyan-600 hover:bg-cyan-500">
              {isUploading ? 'Processing...' : 'Upload to Knowledge Graph'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadPanel;
